import { useEffect, useRef } from 'react';

const COLORS = {
    background: '#e8e4da',
    grid: 'rgba(31, 30, 27, 0.09)',
    ink: '#20201d',
    food: '#bd6c3b',
    vision: 'rgba(38, 67, 48, 0.11)',
    visionLine: 'rgba(38, 67, 48, 0.32)',
};

function drawFood(context, x, y, radius) {
    context.save();
    context.translate(x, y);
    context.rotate(-0.5);
    context.fillStyle = COLORS.food;
    context.beginPath();
    context.ellipse(0, 0, radius * 0.72, radius, 0, 0, Math.PI * 2);
    context.fill();
    context.strokeStyle = 'rgba(32, 32, 29, 0.42)';
    context.lineWidth = Math.max(1, radius * 0.08);
    context.beginPath();
    context.moveTo(0, -radius * 0.8);
    context.lineTo(radius * 0.18, -radius * 1.25);
    context.stroke();
    context.restore();
}

function drawAgent(context, x, y, radius, direction) {
    const angles = [-Math.PI / 2, 0, Math.PI / 2, Math.PI];
    const angle = angles[direction] || 0;

    context.save();
    context.translate(x, y);
    context.fillStyle = COLORS.ink;
    context.beginPath();
    context.arc(0, 0, radius, 0, Math.PI * 2);
    context.fill();

    context.rotate(angle);
    context.fillStyle = COLORS.background;
    context.beginPath();
    context.arc(radius * 0.42, -radius * 0.27, radius * 0.11, 0, Math.PI * 2);
    context.arc(radius * 0.42, radius * 0.27, radius * 0.11, 0, Math.PI * 2);
    context.fill();
    context.restore();
}

function ForageCanvas({ snapshot, vision }) {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const bounds = canvas.getBoundingClientRect();
        const size = Math.max(280, Math.floor(bounds.width));
        const ratio = Math.min(window.devicePixelRatio || 1, 2);
        canvas.width = size * ratio;
        canvas.height = size * ratio;

        const context = canvas.getContext('2d');
        if (!context) return;
        context.scale(ratio, ratio);
        context.clearRect(0, 0, size, size);
        context.fillStyle = COLORS.background;
        context.fillRect(0, 0, size, size);

        const cellSize = size / snapshot.gridSize;
        context.strokeStyle = COLORS.grid;
        context.lineWidth = 1;
        for (let index = 1; index < snapshot.gridSize; index += 1) {
            const position = Math.round(index * cellSize) + 0.5;
            context.beginPath();
            context.moveTo(position, 0);
            context.lineTo(position, size);
            context.stroke();
            context.beginPath();
            context.moveTo(0, position);
            context.lineTo(size, position);
            context.stroke();
        }

        const center = (cell) => (cell + 0.5) * cellSize;
        const agentX = center(snapshot.agent.x);
        const agentY = center(snapshot.agent.y);
        const visionRadius = (vision + 0.5) * cellSize;

        context.fillStyle = COLORS.vision;
        context.strokeStyle = COLORS.visionLine;
        context.setLineDash([4, 5]);
        context.beginPath();
        context.arc(agentX, agentY, visionRadius, 0, Math.PI * 2);
        context.fill();
        context.stroke();
        context.setLineDash([]);

        snapshot.trail.forEach((cell, index) => {
            const progress = (index + 1) / snapshot.trail.length;
            context.fillStyle = `rgba(32, 32, 29, ${progress * 0.18})`;
            context.beginPath();
            context.arc(center(cell.x), center(cell.y), cellSize * 0.07, 0, Math.PI * 2);
            context.fill();
        });

        snapshot.foods.forEach((food) => {
            drawFood(context, center(food.x), center(food.y), cellSize * 0.2);
        });

        drawAgent(
            context,
            agentX,
            agentY,
            cellSize * 0.29,
            snapshot.agent.direction,
        );
    }, [snapshot, vision]);

    return (
        <canvas
            ref={canvasRef}
            className="forage-canvas"
            aria-label="A reinforcement-learning agent searching for food"
        />
    );
}

export default ForageCanvas;
